import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faEdit,
  faEye,
  faStar as faStarSolid,
  faStar as faStarRegular,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';
import { useForm } from 'react-hook-form';
import PDFViewer from './PDFViewer';
import { supabaseAdmin } from '../lib/supabaseClient';
import CompanyAutocomplete from './CompanyAutocomplete';
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
} from '@vis.gl/react-google-maps';

function DriverModal({ isOpen, onClose, driver, trackingRecord, onSave }) {

  const [editMode, setEditMode] = useState({});
  const [isPDFOpen, setIsPDFOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [driverReviews, setDriverReviews] = useState([]);
  const [driverLocations, setDriverLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [documentRequirements, setDocumentRequirements] = useState([]);
  const [loadingRequirements, setLoadingRequirements] = useState(true);
  // Helper function to format date for datetime-local input
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Set default dates: start time 2 days ago, end time today
  const getDefaultStartTime = () => {
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    twoDaysAgo.setHours(0, 0, 0, 0); // Set to start of day
    return formatDateForInput(twoDaysAgo);
  };

  const getDefaultEndTime = () => {
    const today = new Date();
    today.setHours(23, 59, 0, 0); // Set to end of day
    return formatDateForInput(today);
  };

  const [locationStartTime, setLocationStartTime] = useState(
    getDefaultStartTime()
  );
  const [locationEndTime, setLocationEndTime] = useState(getDefaultEndTime());
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [trackingLink, setTrackingLink] = useState(null);
  const mapBounds = useRef(null);
  const [activeTab, setActiveTab] = useState('details');
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [driverLoads, setDriverLoads] = useState([]);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      first_name: driver?.first_name || '',
      last_name: driver?.last_name || '',
      email: driver?.email || '',
      phone_number: driver?.phone_number || '',
      birth_date: driver?.birth_date || '',
      vehicle_type: driver?.vehicle_type || '',
      tow_plate: driver?.tow_plate || '',
      trailer_plate: driver?.trailer_plate || '',
      citizenship: driver?.citizenship || '',
      company: driver?.company || '',
      company_id: driver?.company_id || '',
      license_number: driver?.license_number || '',
      chassis_number: driver?.chassis_number || '',

      //for
      password: '',
      confirm_password: '',

      // Document fields will be initialized dynamically based on document requirements
      truck_brand: driver?.truck_brand || '',
      truck_model: driver?.truck_model || '',
      trailer_brand: driver?.trailer_brand || '',
      trailer_model: driver?.trailer_model || '',
    },
  });

  // Add a flag to track if Google Maps API is loading
  const [isGoogleMapsLoading, setIsGoogleMapsLoading] = useState(false);

  const COUNTRY_CODES = [
    { name: "Türkiye", code: "90", flag: "🇹🇷" },
    { name: "Azerbaycan", code: "994", flag: "🇦🇿" },
    { name: "Beyaz Rusya", code: "375", flag: "🇧🇾" },
    { name: "Gürcistan", code: "995", flag: "🇬🇪" },
    { name: "Kazakistan", code: "7", flag: "🇰🇿" },
    { name: "Kırgızistan", code: "996", flag: "🇰🇬" },
    { name: "Moldova", code: "373", flag: "🇲🇩" },
    { name: "Özbekistan", code: "998", flag: "🇺🇿" },
    { name: "Rusya", code: "7", flag: "🇷🇺" },
    { name: "Tacikistan", code: "992", flag: "🇹🇯" },
    { name: "Türkmenistan", code: "993", flag: "🇹🇲" },
    { name: "Ukrayna", code: "380", flag: "🇺🇦" }
  ];
  const [countryCode, setCountryCode] = useState(COUNTRY_CODES[0]);

  const handleCountryChange = (e) => {
    const country = COUNTRY_CODES.find(c => c.code === e.target.value);
    if (country) setCountryCode(country);
  };

  // Fetch document requirements from the database
  useEffect(() => {
    const fetchDocumentRequirements = async () => {
      try {
        setLoadingRequirements(true);

        // Check if document_requirements table exists
        let documentReqs = [];

        try {
          const { data, error } = await supabase
            .from('document_requirements')
            .select('*')
            .eq('user_type', 'driver')
            .eq('active', true)
            .order('sort_order', { ascending: true });

          if (!error) {
            documentReqs = data || [];
          }
        } catch (tableError) {
          console.log(
            'Document requirements table may not exist yet:',
            tableError
          );
        }

        // If table doesn't exist or no data, use default requirements
        if (documentReqs.length === 0) {
          console.log('Using default document requirements');
          documentReqs = [
            {
              document_key: 'drivers_license',
              name_en: "Driver's License",
              name_tr: 'Sürücü Belgesi',
              name_ru: 'Водительские права',
              is_required: true,
              sort_order: 1,
            },
            {
              document_key: 'vehicle_license',
              name_en: 'Vehicle License',
              name_tr: 'Araç Ruhsatı',
              name_ru: 'Свидетельство о регистрации ТС',
              is_required: true,
              sort_order: 2,
            },
            {
              document_key: 'passport',
              name_en: 'Passport',
              name_tr: 'Pasaport',
              name_ru: 'Паспорт',
              is_required: true,
              sort_order: 3,
            },
            {
              document_key: 'tax_plate',
              name_en: 'Tax Plate',
              name_tr: 'Vergi Levhası',
              name_ru: 'Налоговая табличка',
              is_required: false,
              sort_order: 4,
            },
            {
              document_key: 'transport_license',
              name_en: 'Transport License',
              name_tr: 'Uluslararası Taşıma Yetki Belgesi',
              name_ru: 'Лицензия на перевозку',
              is_required: true,
              sort_order: 5,
            },
            {
              document_key: 'adr_license',
              name_en: 'ADR License',
              name_tr: 'ADR Ehliyeti',
              name_ru: 'Лицензия ADR',
              is_required: false,
              sort_order: 6,
            },
            {
              document_key: 'cmr_insurance',
              name_en: 'CMR Insurance',
              name_tr: 'CMR Sigortası',
              name_ru: 'Страхование CMR',
              is_required: false,
              sort_order: 7,
            },
          ];
        }

        setDocumentRequirements(documentReqs);
      } catch (error) {
        console.error('Error fetching document requirements:', error);
        // Fall back to default requirements on error
        setDocumentRequirements([
          {
            document_key: 'drivers_license',
            name_en: "Driver's License",
            name_tr: 'Sürücü Belgesi',
            name_ru: 'Водительские права',
            is_required: true,
            sort_order: 1,
          },
          {
            document_key: 'vehicle_license',
            name_en: 'Vehicle License',
            name_tr: 'Araç Ruhsatı',
            name_ru: 'Свидетельство о регистрации ТС',
            is_required: true,
            sort_order: 2,
          },
          {
            document_key: 'passport',
            name_en: 'Passport',
            name_tr: 'Pasaport',
            name_ru: 'Паспорт',
            is_required: true,
            sort_order: 3,
          },
          {
            document_key: 'tax_plate',
            name_en: 'Tax Plate',
            name_tr: 'Vergi Levhası',
            name_ru: 'Налоговая табличка',
            is_required: false,
            sort_order: 4,
          },
          {
            document_key: 'transport_license',
            name_en: 'Transport License',
            name_tr: 'Uluslararası Taşıma Yetki Belgesi',
            name_ru: 'Лицензия на перевозку',
            is_required: true,
            sort_order: 5,
          },
          {
            document_key: 'adr_license',
            name_en: 'ADR License',
            name_tr: 'ADR Ehliyeti',
            name_ru: 'Лицензия ADR',
            is_required: false,
            sort_order: 6,
          },
          {
            document_key: 'cmr_insurance',
            name_en: 'CMR Insurance',
            name_tr: 'CMR Sigortası',
            name_ru: 'Страхование CMR',
            is_required: false,
            sort_order: 7,
          },
        ]);
      } finally {
        setLoadingRequirements(false);
      }
    };

    if (isOpen) {
      fetchDocumentRequirements();
    }
  }, [isOpen]);

  const toCamelCase = (str) => {
    return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
  };

  useEffect(() => {
    if (isOpen && documentRequirements.length > 0) {
      if (driver) {
        // Initialize document statuses from driver data based on document requirements
        const initialDocumentStatuses = {};
        documentRequirements.forEach((docReq) => {
          const docKey = docReq.document_key;
          initialDocumentStatuses[docKey] = driver[docKey] || {
            status: 'pending',
            expiry_date: '',
          };
        });
        setDocumentStatuses(initialDocumentStatuses);

        // If editing existing driver, set their values
        const formValues = {
          first_name: driver.first_name || '',
          last_name: driver.last_name || '',
          email: driver.email || '',
          phone_number: driver.phone_number || '',
          birth_date: driver.birth_date || '',
          vehicle_type: driver.vehicle_type || '',
          tow_plate: driver.tow_plate || '',
          trailer_plate: driver.trailer_plate || '',
          citizenship: driver.citizenship || '',
          company: driver.company || '',
          license_number: driver.license_number || '',
          chassis_number: driver.chassis_number || '',
          truck_brand: driver.truck_brand || '',
          truck_model: driver.truck_model || '',
          trailer_brand: driver.trailer_brand || '',
          trailer_model: driver.trailer_model || '',
        };

        // Add dynamic document fields based on requirements
        documentRequirements.forEach((docReq) => {
          const docKey = docReq.document_key;
          formValues[docKey] = {
            status: driver[docKey]?.status || 'pending',
            expiry_date: driver[docKey]?.expiry_date || '',
            url: driver[docKey]?.url || null,
          };
        });

        console.log('====================================');
        console.log(formValues);
        console.log('====================================');

        reset(formValues);
        setTrackingLink(null);
      } else {
        // If adding new driver, reset to empty values
        reset(
          {
            first_name: '',
            last_name: '',
            email: '',
            phone_number: '',
            birth_date: '',
            vehicle_type: '',
            tow_plate: '',
            trailer_plate: '',
            citizenship: '',
            company: '',
            license_number: '',
            chassis_number: '',
            password: '',
            confirm_password: '',
            license_cert: null,
            vehicle_license: null,
            drivers_license: null,
            source_document: null,
            passport: null,
            tax_plate: null,
            transport_license: null,
            adr_license: null,
            cmr_insurance: null,
            truck_brand: '',
            truck_model: '',
            trailer_brand: '',
            trailer_model: '',
          },
          {
            keepDefaultValues: false, // This ensures complete form reset
          }
        );
      }
    }
  }, [isOpen, driver, reset, documentRequirements]);

  useEffect(() => {
    if (isOpen && driver) {
      fetchDriverReviews(driver.id);
      fetchDriverLocations(driver.id);
      fetchDriverLoads(driver.id);
    }
  }, [isOpen, driver]);

  const fetchDriverLoads = async (driverId) => {
    try {
      const { data: loads, error } = await supabase
        .from('loads')
        .select('*')
        .contains('accepted_driver', { id: driverId })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDriverLoads(loads || []);
    } catch (error) {
      console.error('Error fetching driver loads:', error);
      setDriverLoads([]);
    }
  };

  // Add new useEffect to filter locations when filter values or locations change
  useEffect(() => {
    if (driverLocations.length > 0) {
      let filtered = [...driverLocations];

      if (locationStartTime) {
        const startDate = new Date(locationStartTime);
        filtered = filtered.filter(
          (location) => new Date(location.created_at) >= startDate
        );
      }

      if (locationEndTime) {
        const endDate = new Date(locationEndTime);
        filtered = filtered.filter(
          (location) => new Date(location.created_at) <= endDate
        );
      }

      setFilteredLocations(filtered);
    } else {
      setFilteredLocations([]);
    }
  }, [driverLocations, locationStartTime, locationEndTime]);

  const toggleEdit = (fieldName) => {
    setEditMode((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
    if (!editMode[fieldName]) {
      setValue(fieldName, driver[fieldName]);
    }
  };

  useEffect(() => {
    if (driver) {
      setEditMode({});
    } else {
      setEditMode({
        first_name: true,
        last_name: true,
        phone_number: true,
        birth_date: true,
        vehicle_type: true,
        tow_plate: true,
        trailer_plate: true,
        citizenship: true,
        company: true,
        license_number: true,
        chassis_number: true,
        truck_brand: true,
        truck_model: true,
        trailer_brand: true,
        trailer_model: true,
      });
    }
  }, [driver]);

  const uploadDocument = async (file, path) => {
    if (!file) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    try {
      const { error: uploadError, data } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from('documents').getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw new Error(`Error uploading ${path}: ${error.message}`);
    }
  };

  const onSubmit = async (data) => {
    try {
      if (!driver) {
        // Dynamic document uploads based on requirements
        const documentUploads = await Promise.all(
          documentRequirements.map(async (docReq) => {
            const docKey = docReq.document_key;
            const file = data[docKey] ? data[docKey][0] : null;

/*             
            if (docReq.is_required && !file) {
              throw new Error(`${docReq.name_tr} is required`);
            }

 */
            if (file) {
              const url = await uploadDocument(file, `${docKey}s`);
              return { docKey, url };
            }
            return { docKey, url: null };
          })
        );

        // country code ve telefon numarasını birleştirme
        const fullPhone = `${countryCode.code}${data.phone_number}`;

        // Creating new driver
        const { data: authData, error: signUpError } =
          await supabaseAdmin.auth.admin.createUser({
            phone: fullPhone,
            phone_confirm: true,
          });

        if (signUpError && signUpError.code != "phone_exists") throw signUpError;

        const userId = authData?.user?.id || ( await supabaseAdmin.auth.admin
          .listUsers({ phone: data.phone_number })).data.users[0].id;
        

        // Create driver profile with dynamic document data
        const profileData = {
          id: userId,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone_number: fullPhone,
          birth_date: data.birth_date,
          vehicle_type: data.vehicle_type,
          tow_plate: data.tow_plate,
          trailer_plate: data.trailer_plate,
          is_profile_completed: true,
          citizenship: data.citizenship,
          company: data.company,
          license_number: data.license_number,
          chassis_number: data.chassis_number,
          user_type: 'driver',
          status: 'pending',
          truck_brand: data.truck_brand,
          truck_model: data.truck_model,
          trailer_brand: data.trailer_brand,
          trailer_model: data.trailer_model,
        };

        // Add dynamic document fields
        documentUploads.forEach(({ docKey, url }) => {
          if (url) {
            profileData[docKey] = {
              url: url,
              status: 'pending',
              created_at: new Date().toISOString(),
              expiry_date: null,
            };
          }
        });

        console.log('====================================');
        console.log(profileData, "profileData");
        console.log('====================================');

        const { error: profileError } = await supabaseAdmin
          .from('profiles')
          .insert([profileData]);

        if (profileError) {
          if (profileError.code === "23505") { // bu kullanıcı zaten var hatası kodu
            throw new Error("This user already exist");
          }

          throw profileError;
        }

      } else {
        const updateData = {};
        Object.keys(editMode).forEach((field) => {
          if (editMode[field] && data[field] !== driver[field]) {
            updateData[field] = data[field];
          }
        });

        // Handle company selection specially
        if (editMode.company) {
          if (data.company_id) {
            // Fetch the company name from the selected company ID
            const { data: companyData, error: companyError } = await supabase
              .from('profiles')
              .select('company_name')
              .eq('id', data.company_id)
              .single();

            if (companyError) {
              console.error('Error fetching company:', companyError);
            } else if (companyData) {
              updateData.company = companyData.company_name;
              updateData.company_id = data.company_id;
              updateData.user_type = 'company_driver'; // Update user type to company driver
            }
          } else {
            // If company_id is empty or null, remove the company association
            updateData.company = ''; // Use empty string instead of null due to NOT NULL constraint
            updateData.company_id = null;
            updateData.user_type = 'driver'; // Change back to regular driver
          }
        }

        if (Object.keys(updateData).length > 0) {
          updateData.updated_at = new Date();

          const { error } = await supabase
            .from('profiles')
            .update(updateData)
            .eq('id', driver.id);

          if (error) throw error;
        }
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      alert(error.message);
    }
  };

  const handleDocumentStatus = async (docType, status, expiryDate = null) => {
    try {
      // Require expiry date when approving
      if (status === 'approved' && !expiryDate) {
        alert('Please select an expiry date before approving');
        return;
      }

      const currentDocData = documentStatuses[docType] || driver[docType];

      const documentUpdate = {
        ...currentDocData,
        status: status,
        expiry_date: expiryDate,
        answer_date: new Date(),
      };

      // Update form value
      setValue(`${docType}`, documentUpdate);

      // Update local state for immediate UI update
      setDocumentStatuses((prev) => ({
        ...prev,
        [docType]: documentUpdate,
      }));

      const { error } = await supabase
        .from('profiles')
        .update({
          [docType]: documentUpdate,
        })
        .eq('id', driver.id);

      if (error) throw error;

      onSave();
    } catch (error) {
      // Revert to original state on error
      setDocumentStatuses((prev) => ({
        ...prev,
        [docType]: driver[docType],
      }));
      console.error('Error updating document status:', error);
      alert('Failed to update document status');
    }
  };

  const updateUserRole = async (userId, role) => {
    const { error: authError, data } =
      await supabaseAdmin.auth.admin.updateUserById(userId, {
        user_metadata: { role: 'admin' },
      });
    console.log(authError, data);
  };

  const handleSave = () => {
    // TODO: Implement save functionality
    onSave();
    onClose();
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const [expiryDate, setExpiryDate] = useState(new Date());
  const handleViewDocument = (documentUrl) => {
    if (!documentUrl) return;
    setSelectedDocument(documentUrl);
    setIsPDFOpen(true);
  };

  // Handle document upload for existing driver
  const handleDocumentUpload = async (docKey, file) => {
    if (!driver || !file) return;

    try {
      // Upload the document to storage
      const fileUrl = await uploadDocument(file, `${docKey}s`);

      if (!fileUrl) {
        console.error('Failed to upload document');
        return;
      }

      // Create document object
      const documentUpdate = {
        url: fileUrl,
        status: 'pending',
        created_at: new Date().toISOString(),
        expiry_date: null,
      };

      // Update form value
      setValue(`${docKey}`, documentUpdate);

      // Update local state for immediate UI update
      setDocumentStatuses((prev) => ({
        ...prev,
        [docKey]: documentUpdate,
      }));

      // Update the document in the database
      const { error } = await supabase
        .from('profiles')
        .update({
          [docKey]: documentUpdate,
        })
        .eq('id', driver.id);

      if (error) {
        console.error('Error updating document:', error);
        alert('Failed to update document. Please try again.');
        return;
      }

      // Notify parent component about the update
      onSave();
    } catch (error) {
      console.error('Error in document upload process:', error);
      alert('An error occurred during document upload. Please try again.');
    }
  };

  const fetchDriverReviews = async (driverId) => {
    try {
      const { data: reviews, error } = await supabase
        .from('driver_reviews')
        .select(`*`)
        .eq('driver_id', driverId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDriverReviews(reviews);
    } catch (error) {
      console.error('Error fetching driver reviews:', error);
    }
  };

  const fetchDriverLocations = async (driverId) => {
    try {
      // Set loading state
      setDriverLocations([]);
      setFilteredLocations([]);

      const { data: locations, error } = await supabase
        .from('locations')
        .select('*')
        .eq('user_id', driverId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Ensure locations is an array even if null/undefined
      const locationsArray = locations || [];
      setDriverLocations(locationsArray);

      // Set current location (most recent)
      if (locationsArray.length > 0) {
        const latest = locationsArray[0];
        setCurrentLocation({
          lat: latest.latitude || 0,
          lng: latest.longitude || 0,
          created_at: latest.created_at || new Date().toISOString(),
        });
      } else {
        // Default location if none exists
        setCurrentLocation({
          lat: 41.0082, // Default to Istanbul or another city
          lng: 28.9784,
          created_at: new Date().toISOString(),
        });
      }

      // If we're currently on the location tab, try refreshing the map display
      if (activeTab === 'location') {
        // Force a re-render of the map component by triggering state change
        setTimeout(() => {
          setFilteredLocations([...locationsArray]);
        }, 500);
      }
    } catch (error) {
      console.error('Error fetching driver locations:', error);
      // Set default location on error
      setCurrentLocation({
        lat: 41.0082, // Default to Istanbul
        lng: 28.9784,
        created_at: new Date().toISOString(),
      });
    }
  };

  useEffect(() => {
    if (window.google && window.google.maps && filteredLocations.length > 0) {
      mapBounds.current = new window.google.maps.LatLngBounds();
      filteredLocations.forEach((location) => {
        mapBounds.current.extend(
          new window.google.maps.LatLng(location.latitude, location.longitude)
        );
      });
    }
  }, [filteredLocations]);

  const handleMapLoad = useCallback(
    (map) => {
      if (mapBounds.current) {
        map.fitBounds(mapBounds.current);
      }
    },
    [mapBounds]
  );

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Enforce 24-hour format
    });
  };

  // Define the renderMap function that was missing
  const renderMap = () => {
    // Only show loading state briefly
    if (isGoogleMapsLoading) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
          <div className="h-[400px] rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <p>Harita yükleniyor...</p>
          </div>
        </div>
      );
    }

    // If there are no locations, show a message
    if (!driverLocations.length) {
      return (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
          <div className="h-[400px] rounded-lg overflow-hidden flex items-center justify-center bg-gray-100">
            <p>Konum bilgisi bulunamadı</p>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3">Sürücü Konumları</h3>
        <div className="flex space-x-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm text-gray-500 mb-1">
              Başlangıç Zamanı
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border rounded-lg"
              value={locationStartTime}
              onChange={(e) => setLocationStartTime(e.target.value)}
              step="60"
              data-format="24h"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm text-gray-500 mb-1">
              Bitiş Zamanı
            </label>
            <input
              type="datetime-local"
              className="w-full px-3 py-2 border rounded-lg"
              value={locationEndTime}
              onChange={(e) => setLocationEndTime(e.target.value)}
              step="60"
              data-format="24h"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => {
                setLocationStartTime(getDefaultStartTime());
                setLocationEndTime(getDefaultEndTime());
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Filtreyi Temizle
            </button>
          </div>
        </div>
        <div className="h-[400px] rounded-lg overflow-hidden">
          <APIProvider apiKey="AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o">
            <Map
              onLoad={handleMapLoad}
              defaultCenter={{
                lat:
                  currentLocation?.lat ||
                  filteredLocations[0]?.latitude ||
                  41.0082,
                lng:
                  currentLocation?.lng ||
                  filteredLocations[0]?.longitude ||
                  28.9784,
              }}
              defaultZoom={14}
              gestureHandling="greedy"
              options={{
                zoomControl: true,
                mapTypeControl: true,
                scaleControl: true,
                streetViewControl: true,
                rotateControl: true,
                fullscreenControl: true,
              }}
            >
              {/* Render older locations first (they'll be at the bottom) */}
              {filteredLocations.slice(1).map((location, index) => (
                <Marker
                  key={index + 1}
                  position={{ lat: location.latitude, lng: location.longitude }}
                  onClick={() => setShowInfoWindow(index + 1)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" fill="#EF4444" stroke="#DC2626" stroke-width="2"/>
                      </svg>
                    `)}`,
                    scaledSize: { width: 24, height: 24 },
                    anchor: { x: 12, y: 12 },
                  }}
                  options={{
                    zIndex: 1, // Lower z-index for older locations
                  }}
                />
              ))}

              {/* Render the most recent location last (it'll be on top) */}
              {filteredLocations.length > 0 && (
                <Marker
                  key="latest"
                  position={{
                    lat: filteredLocations[0].latitude,
                    lng: filteredLocations[0].longitude,
                  }}
                  onClick={() => setShowInfoWindow(0)}
                  icon={{
                    url: `data:image/svg+xml,${encodeURIComponent(`
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                        <circle cx="14" cy="14" r="12" fill="#3B82F6" stroke="#2563EB" stroke-width="2"/>
                      </svg>
                    `)}`,
                    scaledSize: { width: 28, height: 28 },
                    anchor: { x: 14, y: 14 },
                  }}
                  options={{
                    zIndex: 999, // Higher z-index for the latest location
                  }}
                />
              )}
              {showInfoWindow !== false &&
                filteredLocations[showInfoWindow] && (
                  <InfoWindow
                    position={{
                      lat: filteredLocations[showInfoWindow].latitude,
                      lng: filteredLocations[showInfoWindow].longitude,
                    }}
                    onCloseClick={() => setShowInfoWindow(false)}
                    options={{
                      zIndex: 1000, // Make sure InfoWindow is always on top
                    }}
                  >
                    <div style={{ padding: '8px' }}>
                      <p style={{ margin: '0', fontWeight: 'bold' }}>
                        {showInfoWindow === 0 ? 'Son Konum' : 'Önceki Konum'}
                      </p>
                      <p style={{ margin: '4px 0 0' }}>
                        {formatDate(
                          filteredLocations[showInfoWindow].created_at
                        )}
                      </p>
                    </div>
                  </InfoWindow>
                )}
            </Map>
          </APIProvider>
        </div>
        <div className="flex items-center mt-2 text-sm">
          <div className="flex items-center mr-4">
            <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
            <span>Son Konum</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
            <span>Önceki Konumlar</span>
          </div>
          <div className="ml-auto text-sm text-gray-600">
            {filteredLocations.length} konum gösteriliyor
          </div>
        </div>
      </div>
    );
  };

  const handleCreateTrackingURL = async() => {
    try {
      // Paylaşılan linkin geçerlilik süresi default 10 sene olarak ayarlanıyor.
      const today = new Date();
      const tenYearsLater = new Date();
      tenYearsLater.setFullYear(today.getFullYear() + 10);
      
      const fullName = driver?.first_name + " " + driver?.last_name

      const { data, error } = await supabase
        .from('share_location')
        .insert([
           {
            driver_id: driver?.id,
            name_surname: fullName,
            company_name: driver?.company && driver?.company.trim() !== "" 
              ? driver?.company 
              : "Şirket Bulunamadı",
            title: driver?.user_type,
            start_date: today.toISOString().split('T')[0],
            end_date: tenYearsLater.toISOString().split('T')[0],
            status: true,
          }, 
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const shareableLink = `https://konvoy.global/tracking?key=${data.record_id}`;
      setTrackingLink(shareableLink);
    }
      
    catch (err) {
      console.error('Error sharing location:', err);
      alert("Başarısız")
    }
  };


  // Add a handler for tab changes to ensure Maps API is loaded properly
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);

    // Ensure Google Maps is loaded and locations are fetched when switching to location tab
    if (tabName === 'location' && driver) {
      // Set a loading state first to show progress to prevent stalling
      setIsGoogleMapsLoading(true);

      // Refresh driver locations data when switching to location tab
      fetchDriverLocations(driver.id);

      // Reset loading state after a short delay to ensure map renders
      setTimeout(() => {
        setIsGoogleMapsLoading(false);
      }, 2000);
    } else if (tabName === 'reviews' && driver) {
      // Fetch reviews when switching to reviews tab
      fetchDriverReviews(driver.id);
    } else if (tabName === 'loads' && driver) {
      // Fetch loads when switching to loads tab
      fetchDriverLoads(driver.id);
    }
  };

  // Function to handle removing a user from a company
  const handleRemoveCompany = async () => {
    if (!driver) return;

    if (
      window.confirm(
        'Bu sürücüyü şirketten çıkarmak istediğinize emin misiniz?'
      )
    ) {
      try {
        const updateData = {
          company: '', // Use empty string instead of null due to NOT NULL constraint
          company_id: null,
          user_type: 'driver', // Change back to regular driver
          updated_at: new Date(),
        };

        const { error } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', driver.id);

        if (error) throw error;

        // Update the local state
        setValue('company', '');
        setValue('company_id', null);

        // Update the driver object in the local state to reflect changes immediately
        driver.company = '';
        driver.company_id = null;
        driver.user_type = 'driver';

        // Notify parent component about the update
        onSave();

        // Show success message
        alert('Sürücü şirketten başarıyla çıkarıldı.');
      } catch (error) {
        console.error('Error removing driver from company:', error);
        alert('Sürücüyü şirketten çıkarırken bir hata oluştu.');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="inline-block w-full max-w-4xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg"
        >
          {/* Modal panel */}
          <div className="inline-block w-full max-w-4xl  text-left align-middle transition-all transform bg-white shadow-xl rounded-lg overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Sürücü Detayları
              </h3>
              <button
                onClick={() => {
                  onClose();
                  setActiveTab('details');
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div
              className="overflow-y-auto px-4 py-5"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            >
              {/* Tabs for driver details */}
              {driver && (
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      type="button"
                      onClick={() => handleTabChange('details')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === 'details'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      Detaylar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabChange('location')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === 'location'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      Konum
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabChange('reviews')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === 'reviews'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      Değerlendirmeler
                    </button>
                    <button
                      type="button"
                      onClick={() => handleTabChange('loads')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm
                        ${
                          activeTab === 'loads'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                      Yükler
                    </button>
                  </nav>
                </div>
              )}

              {/* Modal Content */}
              <div className="px-6 flex flex-col max-h-[80vh]">
                {/* Show active tab content */}
                {activeTab === 'loads' && driver && (
                  <>
                    <div className="bg-gray-100 px-4 py-2 -mx-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Sürücü Yükleri
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Referans No
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Yükleme
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Boşaltma
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Müşteri
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Durum
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                Tarih
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {driverLoads.length > 0 ? (
                              driverLoads.map((load) => (
                                <tr key={load.id}>
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-blue-600">
                                      {load.formatted_id}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      {load.loading_country}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {load.pickup_location}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      {load.discharge_country}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {load.delivery_location}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      {load.customer_name}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs ${
                                          load.status === 'completed'
                                            ? 'bg-green-100 text-green-800'
                                            : load.status === 'assigned'
                                              ? 'bg-blue-100 text-blue-800'
                                              : load.status === 'waitingDriver'
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}
                                      >
                                        {load.status === 'completed'
                                          ? 'Tamamlandı'
                                          : load.status === 'assigned'
                                            ? 'Atandı'
                                            : load.status === 'waitingDriver'
                                              ? 'Sürücü Cevabı Bekleniyor'
                                              : load.status}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      {formatDate(load.created_at)}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="px-6 py-4 text-center text-sm text-gray-500"
                                >
                                  Bu sürücüye atanmış yük bulunamadı
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
                {(!driver || activeTab === 'details') && (
                  <>
                    {/* Personal Information */}
                    <div className="bg-gray-100 px-4 py-2 -mx-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Kişisel Bilgiler
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        {/* Full Name */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Ad
                          </label>
                          <div className="flex items-center">
                            {editMode.first_name ? (
                              <input
                                {...register('first_name')}
                                defaultValue={driver?.first_name}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.first_name}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('first_name')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Soyad
                          </label>
                          <div className="flex items-center">
                            {editMode.last_name ? (
                              <input
                                {...register('last_name')}
                                defaultValue={driver?.last_name}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.last_name}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('last_name')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Telefon</label>
                          <div className="flex items-center">
                            {editMode.phone_number ? (
                              <div className="flex items-center gap-2 w-full">
                                <select
                                  value={countryCode.code}
                                  onChange={handleCountryChange}
                                  className="px-2 py-1 border rounded text-sm"
                                >
                                  {COUNTRY_CODES.map((c) => (
                                    <option key={`${c.name}-${c.code}`} value={c.code}>
                                      {c.flag} +{c.code}
                                    </option>
                                  ))}
                                </select>

                                <input
                                  type="tel"
                                  {...register('phone_number', {
                                    required: 'Telefon numarası gerekli',
                                    pattern: {
                                      value: /^[1-9]\d{8,14}$/, // 9-15 hane, başı 0 değil
                                      message: 'Lütfen ülke kodu olmadan telefon numarasını girin (örn: 5551234567)',
                                    },
                                  })}
                                  className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                  placeholder="5551234567"
                                  inputMode="numeric"
                                />
                              </div>
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.phone_number || '-'}
                              </p>
                            )}
                          </div>

                          {errors.phone_number && (
                            <p className="mt-1 text-xs text-red-500">{errors.phone_number.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Uyruk
                          </label>
                          <div className="flex items-center">
                            {editMode.citizenship ? (
                              <input
                                {...register('citizenship')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.citizenship}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.citizenship}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('citizenship')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Şirket
                          </label>
                          <div className="flex items-center">
                            {editMode.company ? (
                              <CompanyAutocomplete
                                value={driver?.company_id || ''}
                                onChange={(companyId) => {
                                  setValue('company_id', companyId);
                                  // Find the company name from the selected ID
                                  if (companyId) {
                                    // We'll fetch the company name in the onSubmit function
                                    // For now, just store the ID
                                  }
                                }}
                                label=""
                                placeholder="Şirket ara..."
                              />
                            ) : (
                              <div className="flex items-center flex-grow">
                                <p className="text-sm text-gray-900 flex-grow">
                                  {driver?.company}
                                </p>
                                {driver?.company && driver?.company_id && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveCompany()}
                                    className="text-red-600 hover:text-red-800 ml-2"
                                    title="Şirketten Çıkar"
                                  >
                                    <FontAwesomeIcon
                                      icon={faTimes}
                                      className="text-sm"
                                    />
                                  </button>
                                )}
                              </div>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('company')}
                                className="text-blue-600 hover:text-blue-800 ml-2"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Şasi Numarası
                          </label>
                          <div className="flex items-center">
                            {editMode.chassis_number ? (
                              <input
                                {...register('chassis_number')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.chassis_number}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.chassis_number}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('chassis_number')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Birth Date */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Doğum Tarihi
                          </label>
                          <div className="flex items-center">
                            {editMode.birth_date ? (
                              <input
                                type="date"
                                {...register('birth_date')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.birth_date ? new Date(driver.birth_date).toISOString().split('T')[0] : ''}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.birth_date ? new Date(driver.birth_date).toLocaleDateString('tr-TR', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                }) : '-'}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('birth_date')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Email */}

                        {/* Ortalama Puan */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Ortalama Puan
                          </label>
                          <div className="flex items-center">
                            <div className="flex items-center">
                              <div className="flex space-x-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <FontAwesomeIcon
                                    key={star}
                                    icon={
                                      star <= Math.round(driver?.average_rating)
                                        ? faStarSolid
                                        : faStarRegular
                                    }
                                    className={
                                      star <= Math.round(driver?.average_rating)
                                        ? 'text-yellow-400'
                                        : 'text-gray-400'
                                    }
                                  />
                                ))}
                              </div>
                              <span className="ml-2 text-sm text-gray-600">
                                {driver?.average_rating
                                  ? `${driver.average_rating} / 5`
                                  : 'Henüz değerlendirilmemiş'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Driver ID (Non-editable) */}
                        {driver && (
                          <div>
                            <label className="block text-sm text-gray-500 mb-1">
                              Sürücü ID
                            </label>
                            <div className="flex items-center">
                              <p className="text-sm text-gray-900">
                                {driver?.id}
                              </p>
                            </div>
                          </div>
                        )}
                        {/* Password fields - only show for new driver */}
                      </div>
                    </div>

                    {/* Vehicle Information */}
                    <div className="bg-gray-100 px-4 py-2 -mx-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Araç Bilgileri
                      </h3>
                    </div>
                    <div className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                        {/* Vehicle Type */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Araç Tipi
                          </label>
                          <div className="flex items-center">
                            {editMode.vehicle_type ? (
                              <select
                                {...register('vehicle_type')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.vehicle_type}
                              >
                                <option value="">Tüm Tip</option>
                                <option value="closed_box">Closed Box</option>
                                <option value="standart_curtain">Standard Curtain</option>
                                <option value="refrigerated">Refrigerated</option>
                                <option value="adr">ADR</option>
                                <option value="mega_curtain">Mega Curtain</option>
                                <option value="truck&trailer">Truck & Trailer</option>
                                <option value="minivan">Minivan</option>
                                <option value="lorry">Lorry</option>
                                <option value="out_of_gauge">Out of Gauge (OOG)</option>
                                <option value="flatbed">Flatbed</option>
                                <option value="lowbed">Lowbed</option>
                                <option value="jumbo Trailer">Jumbo Trailer</option>
                                <option value="tanker">Tanker</option>
                                <option value="tilt_trailer">Tilt Trailer</option>
                              </select>
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.vehicle_type}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('vehicle_type')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* License Plate */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Çekici Plakası
                          </label>
                          <div className="flex items-center">
                            {editMode.tow_plate ? (
                              <input
                                type="text"
                                {...register('tow_plate')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.tow_plate}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.tow_plate}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('tow_plate')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Trailer Plate */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Dorse Plakası
                          </label>
                          <div className="flex items-center">
                            {editMode.trailer_plate ? (
                              <input
                                type="text"
                                {...register('trailer_plate')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.trailer_plate}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.trailer_plate}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('trailer_plate')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Add new truck fields */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Çekici Markası
                          </label>
                          <div className="flex items-center">
                            {editMode.truck_brand ? (
                              <input
                                type="text"
                                {...register('truck_brand')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.truck_brand}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.truck_brand}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('truck_brand')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Çekici Modeli
                          </label>
                          <div className="flex items-center">
                            {editMode.truck_model ? (
                              <input
                                type="text"
                                {...register('truck_model')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.truck_model}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.truck_model}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('truck_model')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Add new trailer fields */}
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Dorse Markası
                          </label>
                          <div className="flex items-center">
                            {editMode.trailer_brand ? (
                              <input
                                type="text"
                                {...register('trailer_brand')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.trailer_brand}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.trailer_brand}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('trailer_brand')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm text-gray-500 mb-1">
                            Dorse Modeli
                          </label>
                          <div className="flex items-center">
                            {editMode.trailer_model ? (
                              <input
                                type="text"
                                {...register('trailer_model')}
                                className={`text-sm w-full ${driver ? 'mr-2' : ''} px-2 py-1 border rounded`}
                                defaultValue={driver?.trailer_model}
                              />
                            ) : (
                              <p className="text-sm text-gray-900 flex-grow">
                                {driver?.trailer_model}
                              </p>
                            )}
                            {driver && (
                              <button
                                type="button"
                                onClick={() => toggleEdit('trailer_model')}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <FontAwesomeIcon
                                  icon={faEdit}
                                  className="text-sm"
                                />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Documents Section */}

                    {!driver && (
                      <>
                        <div className="bg-gray-100 px-4 py-2 -mx-6">
                          <h3 className="text-lg font-medium text-gray-900">
                            Gerekli Belgeler
                          </h3>
                        </div>
                        {loadingRequirements ? (
                          <div className="p-4 text-center">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <p className="mt-2 text-gray-600">
                              Belgeler yükleniyor...
                            </p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-4 p-4">
                            {/* Dynamic document upload fields */}
                            {documentRequirements.map((docReq) => (
                              <DocumentUploadField
                                key={docReq.document_key}
                                label={`${docReq.name_tr}${docReq.is_required ? ' *' : ''}`}
                                register={register}
                                name={docReq.document_key}
                                errors={errors}
                                //required={docReq.is_required}
                                required={false}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {driver && (
                      <>
                        <div className="bg-gray-100 px-4 py-2 -mx-6">
                          <h3 className="text-lg font-medium text-gray-900">
                            Belgeler
                          </h3>
                        </div>
                        {loadingRequirements ? (
                          <div className="p-6 text-center">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            <p className="mt-2 text-gray-600">
                              Belgeler yükleniyor...
                            </p>
                          </div>
                        ) : (
                          <div className="p-6 space-y-4 flex-1">
                            {documentRequirements.map((docReq) => (
                              <DocumentApprovalCard
                                key={docReq.document_key}
                                title={`${docReq.name_tr}${docReq.is_required ? ' *' : ''}`}
                                document={
                                  documentStatuses[docReq.document_key] ||
                                  driver[docReq.document_key]
                                }
                                onView={() => {
                                  // Use the document from documentStatuses if available, otherwise fall back to driver
                                  const docToView =
                                    documentStatuses[docReq.document_key] ||
                                    driver[docReq.document_key];
                                  handleViewDocument(docToView?.url);
                                }}
                                onApprove={() =>
                                  handleDocumentStatus(
                                    docReq.document_key,
                                    'approved',
                                    watch(`${docReq.document_key}.expiry_date`)
                                  )
                                }
                                onReject={() =>
                                  handleDocumentStatus(
                                    docReq.document_key,
                                    'rejected'
                                  )
                                }
                                onUpload={handleDocumentUpload}
                                register={register}
                                docKey={docReq.document_key}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}

                {activeTab === "location" && (
                  <>
                    {renderMap()}

                    <div className="flex flex-col items-center">
                      {trackingRecord ? (
                        <a
                          href={`https://konvoy.global/tracking?key=${trackingRecord}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 text-blue-700 font-medium break-all underline"
                        >
                          Konumu takip et
                        </a>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleCreateTrackingURL}
                            className="cursor-pointer bg-blue-500 text-white px-3 py-1 mt-5 rounded text-sm hover:bg-blue-600 inline-block"
                          >
                            Konum Paylaşma Bağlantısı Oluştur
                          </button>

                          {trackingLink && (
                            <a
                              href={trackingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-3 text-blue-700 font-medium break-all underline"
                            >
                              Konumu takip et
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'reviews' && (
                  <div className="p-6 space-y-4">
                    {driverReviews.length > 0 ? (
                      driverReviews.map((review) => (
                        <div
                          key={review.id}
                          className="bg-white rounded-lg shadow p-4"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="flex space-x-1 mb-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <FontAwesomeIcon
                                    key={star}
                                    icon={
                                      star <= review.rating
                                        ? faStarSolid
                                        : faStarRegular
                                    }
                                    className={
                                      star <= review.rating
                                        ? 'text-yellow-400'
                                        : 'text-gray-400'
                                    }
                                  />
                                ))}
                              </div>
                              <p className="text-gray-700">{review.comment}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">
                                Yük: {review?.load?.formatted_id}
                              </p>
                              <p className="text-sm text-gray-500">
                                {formatDate(review?.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-gray-600">
                            Değerlendiren: {review?.reviewer?.first_name}{' '}
                            {review?.reviewer?.last_name}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        Bu sürücü için henüz değerlendirme yapılmamış.
                      </p>
                    )}
                  </div>
                )}
                {/* PDF Viewer Modal */}
                <PDFViewer
                  isOpen={isPDFOpen}
                  onClose={() => setIsPDFOpen(false)}
                  documentUrl={selectedDocument}
                />
              </div>
            </div>
            {/* Modal Footer */}
            <div className="sticky bottom-0 z-20 bg-white px-4 py-3 border-t">
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    setActiveTab('details');
                  }}
                  className="px-4 py-2 border text-gray-700 rounded-md hover:bg-gray-100"
                >
                  Kapat
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {driver ? 'Değişiklikleri Kaydet' : 'Sürücü Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

const getStatusStyle = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const DocumentUploadField = ({
  label,
  register,
  name,
  errors,
  required = false,
}) => (
  <div>
    <label className="block text-sm text-gray-500 mb-1">{label}</label>
    <input
      type="file"
      accept=".pdf,.jpg,.jpeg,.png"
      {...register(name, {
        required: required ? `${label} is required` : false,
      })}
      className="text-sm w-full px-2 py-1 border rounded"
    />
    {errors[name] && (
      <p className="mt-1 text-xs text-red-500">{errors[name].message}</p>
    )}
  </div>
);

const DocumentApprovalCard = ({
  title,
  document,
  onView,
  onApprove,
  onReject,
  register,
  docKey,
  onUpload,
}) => {
  // Create a ref for the file input
  const fileInputRef = React.useRef(null);

  // Function to handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      onUpload(docKey, e.target.files[0]);
    }
  };

  return (
    <div className="bg-gray-50 p-4 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h5 className="font-medium text-gray-900">{title}</h5>
          {document?.created_at && (
            <p className="text-sm text-gray-500">
              Yüklü: {document.created_at}
            </p>
          )}
          {document?.status === 'approved' && document?.expiry_date && (
            <p className="text-sm text-gray-500">
              Son Geçerlilik:{' '}
              {new Date(document.expiry_date).toLocaleDateString('tr-TR')}
            </p>
          )}
        </div>
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(document?.status)}`}
        >
          {document?.status === 'pending'
            ? 'Onay Bekliyor'
            : document?.status === 'approved'
              ? 'Onaylandı'
              : 'Reddedildi'}
        </span>
      </div>

      <div className="flex flex-col space-y-3">
        <div className="flex items-center justify-between">
          {document?.url ? (
            <button
              type="button"
              onClick={onView}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              <FontAwesomeIcon icon={faEye} className="mr-1" />
              Belgeyi Görüntüle
            </button>
          ) : (
            <span className="text-sm text-gray-500">Belge yüklenmemiş</span>
          )}

          {document?.status !== 'approved' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                {...register(`${docKey}.expiry_date`)}
                className="text-sm border rounded px-2 py-1"
              />
              <button
                onClick={onApprove}
                type="button"
                disabled={!document?.url}
                className={`bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 ${!document?.url ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Onayla
              </button>
              <button
                onClick={onReject}
                type="button"
                className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600"
              >
                Reddet
              </button>
            </div>
          )}
        </div>

        {/* Document upload section */}
        <div className="mt-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.jpg,.jpeg,.png"
            className="hidden"
            id={`file-upload-${docKey}`}
          />
          <label
            htmlFor={`file-upload-${docKey}`}
            className="cursor-pointer bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 inline-block"
          >
            {document?.url ? 'Belgeyi Güncelle' : 'Belge Yükle'}
          </label>
        </div>
      </div>
    </div>
  );
};

export default DriverModal;